import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
export function ServicesOverview() {
  const mainServices = [{
    id: 'ombuds',
    number: '01',
    title: 'External Ombuds & Conflict Resolution',
    description: 'Independent, impartial and confidential support to help people raise concerns, navigate conflict and resolve issues before they escalate.',
    inclusions: ['External ombuds support', 'Mediation & facilitated conversations', 'Conflict coaching', 'Confidential reporting pathways', 'Systemic issue analysis & reporting'],
    cta: 'Explore conflict resolution',
    accent: 'from-brand-magenta to-brand-plum',
    bestFor: 'HR Directors, CEOs, Legal teams managing live conflict or building early intervention systems'
  }, {
    id: 'culture',
    number: '02',
    title: 'Culture, DEI & Workplace Transformation',
    description: 'Practical culture consulting that moves inclusion, fairness and belonging from policy into everyday behaviour.',
    inclusions: ['Culture audits & diagnostics', 'DEI strategy development', 'Inclusion & belonging workshops', 'Systems review & redesign', 'Leadership alignment & capability'],
    cta: 'Explore culture consulting',
    accent: 'from-brand-teal to-brand-deep-purple',
    bestFor: 'People & Culture leaders, Executives driving cultural transformation or DEI initiatives'
  }, {
    id: 'leadership',
    number: '03',
    title: 'Leadership Coaching & Development',
    description: 'Equip leaders with the confidence, skill and emotional intelligence to lead through complexity, conflict and change.',
    inclusions: ['Executive & leadership coaching', 'Team development programs', 'Hard conversation capability', 'Communication & trust-building', 'Change leadership support'],
    cta: 'Explore leadership development',
    accent: 'from-brand-orange to-brand-magenta',
    bestFor: 'Senior leaders, emerging leaders, teams navigating change or building capability'
  }];
  return <section id="services" className="py-24 bg-brand-charcoal text-white relative overflow-hidden" data-id="element-418">
      {/* Subtle background glow */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-deep-purple/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-419"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-brand-teal/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" data-id="element-420"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-421">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-16" data-id="element-422">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-6 leading-tight" data-id="element-423">
            Three ways we help organisations move from tension to clarity.
          </h2>
          <p className="text-xl text-white/90 font-medium leading-relaxed border-l-4 border-brand-magenta pl-6" data-id="element-424">
            Most organisations contact us when an issue is already in motion.
            The earlier we engage, the more options you have.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-10" data-id="element-425">
          {mainServices.map((service, index) => <motion.div key={service.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm hover:bg-white/10 transition-all flex flex-col h-full overflow-hidden group" data-id="element-426">
              {/* Gradient Top Bar */}
              <div className={`h-2 bg-gradient-to-r ${service.accent}`} data-id="element-427"></div>

              <div className="p-8 flex flex-col flex-grow relative" data-id="element-428">
                {/* Large Number Watermark */}
                <div className="absolute top-4 right-4 text-8xl font-serif text-white/5 leading-none select-none" data-id="element-429">
                  {service.number}
                </div>

                <h3 className="text-2xl font-serif mb-4 relative z-10 group-hover:text-brand-lime transition-colors" data-id="element-430">
                  {service.title}
                </h3>
                <p className="text-white/90 mb-8 text-[15px] leading-relaxed font-normal" data-id="element-431">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-8 flex-grow" data-id="element-432">
                  {service.inclusions.map((item, i) => <li key={i} className="flex items-start gap-3 text-[15px] text-white/90 font-normal" data-id="element-433">
                      <CheckCircle2 size={18} className="text-brand-lime shrink-0 mt-0.5" data-id="element-434" />
                      <span data-id="element-435">{item}</span>
                    </li>)}
                </ul>

                <div className="pt-6 border-t border-white/10 mb-8" data-id="element-436">
                  <p className="text-xs text-white/70 mb-2 uppercase tracking-wider font-semibold" data-id="element-437">
                    Best for:
                  </p>
                  <p className="text-[15px] text-white/90 leading-relaxed font-medium" data-id="element-438">
                    {service.bestFor}
                  </p>
                </div>

                <a href={`#${service.id}`} className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-3 rounded-sm transition-all mt-auto w-full group-hover:border-brand-lime/50" data-id="element-439">
                  {service.cta}{' '}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-440" />
                </a>
              </div>
            </motion.div>)}
        </div>

        {/* 4th Supporting Service */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="bg-gradient-to-r from-brand-plum/20 to-brand-deep-purple/20 border border-brand-plum/30 p-8 rounded-sm flex flex-col md:flex-row items-center justify-between gap-6 backdrop-blur-sm" data-id="element-441">
          <div className="max-w-3xl" data-id="element-442">
            <h3 className="text-2xl font-serif mb-3" data-id="element-443">
              Complementary Advisory & Workplace Assessments
            </h3>
            <p className="text-white/90 text-[15px] leading-relaxed font-normal" data-id="element-444">
              Policy development, workplace assessments and practical advisory
              support for organisations that need structure, clarity and
              implementation guidance.
            </p>
          </div>
          <a href="#advisory" className="shrink-0 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3 rounded-sm text-sm font-semibold transition-all" data-id="element-445">
            Learn more <ArrowRight size={16} data-id="element-446" />
          </a>
        </motion.div>
      </div>
    </section>;
}